/**
 * Compute mileage positioning statistics
 * @param {number} targetMileage - Target mileage
 * @param {Array} active_listings - Active listings with miles property
 * @param {Array} sold_listings - Sold listings with miles property
 * @returns {Object} { percentileHigher, q1, median, q3, min, max }
 */
export function computeMileageStats(targetMileage, active_listings = [], sold_listings = []) {
  // Combine all mileages from both active and sold listings
  const allMileages = [
    ...(active_listings || []).map(l => l.miles || l.mileage).filter(m => m != null && m > 0),
    ...(sold_listings || []).map(l => l.miles || l.mileage).filter(m => m != null && m > 0)
  ];

  if (allMileages.length === 0) {
    return {
      percentileHigher: null,
      q1: null,
      median: null,
      q3: null,
      min: null,
      max: null
    };
  }

  // Sort mileages
  const sortedMileages = [...allMileages].sort((a, b) => a - b);
  
  const min = sortedMileages[0];
  const max = sortedMileages[sortedMileages.length - 1];
  
  // Calculate quartiles
  const q1Index = Math.floor(sortedMileages.length * 0.25);
  const medianIndex = Math.floor(sortedMileages.length * 0.5);
  const q3Index = Math.floor(sortedMileages.length * 0.75);
  
  const q1 = sortedMileages[q1Index];
  const median = sortedMileages.length % 2 === 0
    ? (sortedMileages[medianIndex - 1] + sortedMileages[medianIndex]) / 2
    : sortedMileages[medianIndex];
  const q3 = sortedMileages[q3Index];

  // Calculate percentile: % of vehicles with miles < targetMileage
  const countBelow = sortedMileages.filter(m => m < targetMileage).length;
  const countAbove = sortedMileages.filter(m => m > targetMileage).length;
  const countEqual = sortedMileages.filter(m => m === targetMileage).length;
  
  // Calculate what percentage of vehicles have lower/higher mileage
  const percentLower = (countBelow / sortedMileages.length) * 100;
  const percentHigher = (countAbove / sortedMileages.length) * 100;
  
  // Determine if target has higher or lower mileage than most vehicles
  // If more vehicles have higher mileage, target has LOWER mileage
  // If more vehicles have lower mileage, target has HIGHER mileage
  const isHigherMileage = countBelow >= countAbove;
  const percentileHigher = isHigherMileage 
    ? Math.round(percentLower)  // Higher than X% (where X% have lower mileage)
    : Math.round(percentHigher); // Actually lower, but we'll handle this in the component

  return {
    percentileHigher,
    isHigherMileage, // Add flag to indicate direction
    percentLower: Math.round(percentLower),
    percentHigher: Math.round(percentHigher),
    q1,
    median,
    q3,
    min,
    max
  };
}

/**
 * Compute typical sold DOM for similar vehicles
 * @param {number} targetMileage - Target mileage
 * @param {Array} sold_listings - Sold listings with miles and dom properties
 * @returns {Object} { typicalDom, marketAvgDom, windowUsed }
 */
export function computeTypicalSoldDom(targetMileage, sold_listings = []) {
  if (!sold_listings || sold_listings.length === 0) {
    return {
      typicalDom: null,
      marketAvgDom: null,
      windowUsed: null
    };
  }

  // Filter sold listings near target mileage (±15%)
  const mileageWindow = targetMileage * 0.15;
  let soldNear = sold_listings.filter(listing => {
    const miles = listing.miles || listing.mileage || 0;
    return Math.abs(miles - targetMileage) <= mileageWindow;
  });

  let windowUsed = 15;

  // Calculate median DOM for sold near
  let typicalDom = null;
  if (soldNear.length > 0) {
    const doms = soldNear
      .map(listing => listing.dom || listing.dom_active || 0)
      .filter(d => d > 0)
      .sort((a, b) => a - b);
    
    if (doms.length > 0) {
      const mid = Math.floor(doms.length / 2);
      typicalDom = doms.length % 2 === 0
        ? Math.round((doms[mid - 1] + doms[mid]) / 2)
        : Math.round(doms[mid]);
    }
  }

  // Calculate market average DOM (median of all sold listings)
  const allDoms = sold_listings
    .map(listing => listing.dom || listing.dom_active || 0)
    .filter(d => d > 0)
    .sort((a, b) => a - b);
  
  let marketAvgDom = null;
  if (allDoms.length > 0) {
    const mid = Math.floor(allDoms.length / 2);
    marketAvgDom = allDoms.length % 2 === 0
      ? Math.round((allDoms[mid - 1] + allDoms[mid]) / 2)
      : Math.round(allDoms[mid]);
  }

  return {
    typicalDom,
    marketAvgDom,
    windowUsed: soldNear.length >= 3 ? windowUsed : null // Always 15% now
  };
}

/**
 * Compute dealer type split statistics
 * @param {Array} sold_listings - Sold listings with dealer_type and dom properties
 * @returns {Object} { franchisePct, independentPct, franchiseDom, independentDom }
 */
export function computeDealerTypeSplit(sold_listings = []) {
  if (!sold_listings || sold_listings.length === 0) {
    return {
      franchisePct: null,
      independentPct: null,
      franchiseDom: null,
      independentDom: null
    };
  }

  // Filter listings with dealer_type
  const withDealerType = sold_listings.filter(l => l.dealer_type);

  if (withDealerType.length === 0) {
    return {
      franchisePct: null,
      independentPct: null,
      franchiseDom: null,
      independentDom: null
    };
  }

  // Count by dealer type
  const franchiseListings = withDealerType.filter(l => l.dealer_type === "franchise");
  const independentListings = withDealerType.filter(l => l.dealer_type === "independent");

  const total = withDealerType.length;
  const franchiseCount = franchiseListings.length;
  const independentCount = independentListings.length;

  const franchisePct = total > 0 ? Math.round((franchiseCount / total) * 100) : 0;
  const independentPct = total > 0 ? Math.round((independentCount / total) * 100) : 0;

  // Calculate median DOM for each type
  const franchiseDoms = franchiseListings
    .map(l => l.dom || l.dom_active || 0)
    .filter(d => d > 0)
    .sort((a, b) => a - b);
  
  const independentDoms = independentListings
    .map(l => l.dom || l.dom_active || 0)
    .filter(d => d > 0)
    .sort((a, b) => a - b);

  let franchiseDom = null;
  if (franchiseDoms.length > 0) {
    const mid = Math.floor(franchiseDoms.length / 2);
    franchiseDom = franchiseDoms.length % 2 === 0
      ? Math.round((franchiseDoms[mid - 1] + franchiseDoms[mid]) / 2)
      : Math.round(franchiseDoms[mid]);
  }

  let independentDom = null;
  if (independentDoms.length > 0) {
    const mid = Math.floor(independentDoms.length / 2);
    independentDom = independentDoms.length % 2 === 0
      ? Math.round((independentDoms[mid - 1] + independentDoms[mid]) / 2)
      : Math.round(independentDoms[mid]);
  }

  return {
    franchisePct,
    independentPct,
    franchiseDom,
    independentDom
  };
}

/**
 * Compute active dealer type split statistics
 * @param {Array} active_listings - Active listings with dealer_type and dom_active properties
 * @returns {Object} { franchisePct, independentPct, franchiseDom, independentDom }
 */
export function computeActiveDealerTypeSplit(active_listings = []) {
  if (!active_listings || active_listings.length === 0) {
    return {
      franchisePct: null,
      independentPct: null,
      franchiseDom: null,
      independentDom: null
    };
  }

  // Filter listings with dealer_type
  const withDealerType = active_listings.filter(l => l.dealer_type);

  if (withDealerType.length === 0) {
    return {
      franchisePct: null,
      independentPct: null,
      franchiseDom: null,
      independentDom: null
    };
  }

  // Count by dealer type
  const franchiseListings = withDealerType.filter(l => l.dealer_type === "franchise");
  const independentListings = withDealerType.filter(l => l.dealer_type === "independent");

  const total = withDealerType.length;
  const franchiseCount = franchiseListings.length;
  const independentCount = independentListings.length;

  const franchisePct = total > 0 ? Math.round((franchiseCount / total) * 100) : 0;
  const independentPct = total > 0 ? Math.round((independentCount / total) * 100) : 0;

  // Calculate median DOM for each type (using dom_active for active listings)
  const franchiseDoms = franchiseListings
    .map(l => l.dom_active || l.dom || 0)
    .filter(d => d > 0)
    .sort((a, b) => a - b);
  
  const independentDoms = independentListings
    .map(l => l.dom_active || l.dom || 0)
    .filter(d => d > 0)
    .sort((a, b) => a - b);

  let franchiseDom = null;
  if (franchiseDoms.length > 0) {
    const mid = Math.floor(franchiseDoms.length / 2);
    franchiseDom = franchiseDoms.length % 2 === 0
      ? Math.round((franchiseDoms[mid - 1] + franchiseDoms[mid]) / 2)
      : Math.round(franchiseDoms[mid]);
  }

  let independentDom = null;
  if (independentDoms.length > 0) {
    const mid = Math.floor(independentDoms.length / 2);
    independentDom = independentDoms.length % 2 === 0
      ? Math.round((independentDoms[mid - 1] + independentDoms[mid]) / 2)
      : Math.round(independentDoms[mid]);
  }

  return {
    franchisePct,
    independentPct,
    franchiseDom,
    independentDom
  };
}

